import { useEffect, useState } from 'react'
import type { Video } from '@nhanho/shared'
import { videos as api, explainError } from '../../lib/api'
import { useList } from '../../lib/useList'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Field } from '../../components/ui/Field'
import { Badge } from '../../components/ui/Badge'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { EmptyState } from '../../components/ui/EmptyState'
import { Spinner } from '../../components/ui/Spinner'

const EMPTY = { title: '', url: '' }

export default function VideoList() {
  const { data, loading, refresh, params, set } = useList<Video>((p) => api.list({ ...p, limit: 100 }))
  const toast = useToast()
  const [editing, setEditing] = useState<Video | 'new' | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [del, setDel] = useState<Video | null>(null)
  const [q, setQ] = useState(String(params.search ?? ''))
  const dq = useDebounced(q, 300)
  useEffect(() => { if (dq !== String(params.search ?? '')) set({ search: dq }) }, [dq]) // eslint-disable-line react-hooks/exhaustive-deps

  const open = (v: Video | 'new') => { setEditing(v); setForm(v === 'new' ? EMPTY : { title: v.title, url: v.url }); setErrors({}) }

  const save = async () => {
    setBusy(true); setErrors({})
    try {
      if (editing === 'new') await api.create(form); else if (editing) await api.update(editing.id, form)
      toast.success('Đã lưu video'); setEditing(null); refresh()
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
    } finally { setBusy(false) }
  }
  const remove = async () => {
    if (!del) return
    setBusy(true)
    try { await api.remove(del.id); toast.success('Đã xoá'); setDel(null); refresh() }
    catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader title="Video" subtitle="Nhúng từ YouTube / Vimeo — không lưu file video" actions={<Button onClick={() => open('new')}>+ Thêm video</Button>} />
      <div className="mb-4 max-w-sm"><Input placeholder="Tìm theo tiêu đề…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      {loading && !data ? <Spinner /> : !data?.docs.length ? (
        <EmptyState title="Chưa có video" hint="Dán link YouTube hoặc Vimeo để thêm." action={<Button onClick={() => open('new')}>+ Thêm video</Button>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.docs.map((v) => (
            <div key={v.id} className="overflow-hidden rounded-card border border-border bg-white shadow-card">
              <div className="aspect-video bg-sand">
                {v.thumbnailUrl ? <img src={v.thumbnailUrl} alt="" className="size-full object-cover" loading="lazy" /> : <div className="grid size-full place-items-center text-ink-faint">▶</div>}
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="truncate text-sm font-medium text-ink">{v.title}</div><a href={v.url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs text-ink-muted hover:underline">{v.url}</a></div>
                  <Badge tone="primary">{v.provider}</Badge>
                </div>
                <div className="mt-2 flex gap-1"><Button variant="ghost" size="sm" onClick={() => open(v)}>Sửa</Button><Button variant="ghost" size="sm" className="text-danger" onClick={() => setDel(v)}>Xoá</Button></div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? 'Thêm video' : 'Sửa video'}
        footer={<><Button variant="secondary" onClick={() => setEditing(null)}>Huỷ</Button><Button onClick={save} loading={busy}>Lưu</Button></>}>
        <div className="space-y-4">
          <Field label="Tiêu đề" required error={errors.title}><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus /></Field>
          <Field label="Link YouTube / Vimeo" required error={errors.url} hint="Hệ thống tự nhận diện và lấy thumbnail"><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" /></Field>
        </div>
      </Modal>
      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove} loading={busy} title="Xoá video" message={<>Xoá video <strong>{del?.title}</strong>?</>} />
    </>
  )
}
